import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useAssets, type AssetListRow } from "@/features/assets/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, GitMerge } from "lucide-react";
import { AllocateAssetDialog } from "@/features/assets/AllocateAssetDialog";

export const Route = createFileRoute("/_authenticated/integrated-assets")({
  component: IntegratedAssetsPage,
});

function IntegratedAssetsPage() {
  const [page, setPage] = useState(1);
  const pageSize = 25;
  
  const { data, isLoading } = useAssets({
    isIntegrated: true,
    includeInStore: true,
    page,
    pageSize,
  });

  const [toAllocate, setToAllocate] = useState<AssetListRow | null>(null);

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto animate-in fade-in zoom-in-95">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground tracking-tight">
            Integrated Assets
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Review and allocate assets imported from external systems.
          </p>
        </div>
      </div>

      <Card className="overflow-hidden border shadow-sm">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Asset Code</TableHead>
                <TableHead>Asset Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Asset Type</TableHead>
                <TableHead>Make / Model</TableHead>
                <TableHead>Serial No</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-32 text-center">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : data?.rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-32 text-center text-muted-foreground">
                    No integrated assets pending allocation.
                  </TableCell>
                </TableRow>
              ) : (
                data?.rows.map((asset) => (
                  <TableRow key={asset.id} className="group hover:bg-muted/30 transition-colors">
                    <TableCell className="font-medium">{asset.asset_code}</TableCell>
                    <TableCell className="font-medium">{asset.category?.name || "—"}</TableCell>
                    <TableCell className="text-muted-foreground max-w-[200px] truncate" title={asset.name}>{asset.name}</TableCell>
                    <TableCell><Badge variant="secondary" className="font-normal">{asset.asset_class || "—"}</Badge></TableCell>
                    <TableCell>
                      {asset.make || "—"} {asset.model ? `/ ${asset.model}` : ""}
                    </TableCell>
                    <TableCell>{asset.serial_number || "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="secondary"
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => setToAllocate(asset)}
                      >
                        <GitMerge className="h-4 w-4 mr-2" />
                        Allocate
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        
        {/* Simple Pagination */}
        {data && data.total > pageSize && (
          <div className="border-t p-4 flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, data.total)} of {data.total}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page * pageSize >= data.total}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Allocation Modal */}
      <AllocateAssetDialog
        open={!!toAllocate}
        onOpenChange={(open) => !open && setToAllocate(null)}
        asset={toAllocate}
      />
    </div>
  );
}
